import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  LogOut,
  Plus,
  Save,
  Trash2,
  Edit3,
  Eye,
  EyeOff,
  Users,
  FileText,
  ArrowLeft,
  Upload,
  Image as ImageIcon,
  Settings as SettingsIcon,
  Check,
  X as XIcon,
  ChevronUp,
  ChevronDown,
  Star,
  Link2,
  Copy,
  Trash,
} from "lucide-react";
import RichTextEditor from "../components/RichTextEditor";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import {
  adminListPosts,
  adminCreatePost,
  adminUpdatePost,
  adminDeletePost,
  adminReorderPosts,
  adminListSubscribers,
  adminAddSubscriber,
  adminUpdateSubscriber,
  adminDeleteSubscriber,
  adminUploadImage,
  adminUpdateSettings,
  resolveImageUrl,
  buildPreviewUrl,
} from "../lib/api";

const EMPTY_POST = {
  title: "",
  snippet: "",
  tag: "Notes",
  thumb: "",
  body: "",
  published: true,
  notify: true,
  featured: false,
  cover_caption: "",
  display_date: "",
  author_name_override: "",
  author_image_override: "",
  author_bio_override: "",
  author_linkedin_override: "",
  hide_community: false,
  meta_title: "",
  meta_description: "",
  og_title: "",
  og_description: "",
  og_image: "",
  embeds: [],
  lead_magnet_url: "",
  lead_magnet_label: "",
};

export default function Admin() {
  const { user, logout } = useAuth();
  const { settings, refresh: refreshSettings } = useSettings();
  const navigate = useNavigate();
  const [tab, setTab] = useState("posts"); // posts | subscribers | site
  const [posts, setPosts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadPosts = useCallback(async () => {
    try {
      const data = await adminListPosts();
      setPosts(data);
    } catch (e) {
      // 401 → logout handled below
    }
  }, []);

  const loadSubs = useCallback(async () => {
    try {
      const data = await adminListSubscribers();
      setSubscribers(data);
    } catch (e) {
      /* noop */
    }
  }, []);

  useEffect(() => {
    if (user && user !== false) {
      loadPosts();
      loadSubs();
    }
  }, [user, loadPosts, loadSubs]);

  if (user === null) {
    return (
      <div className="h-screen grid place-items-center text-zinc-400 font-display text-sm uppercase tracking-[0.2em]">
        Loading…
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;

  const startNew = () => {
    setEditing({ ...EMPTY_POST });
    setPreviewMode(false);
  };

  const startEdit = (p) => {
    setEditing({ ...p });
    setPreviewMode(false);
  };

  const cancelEdit = () => {
    setEditing(null);
    setError("");
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.title.trim()) {
      setError("Title is required");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (editing.id) {
        await adminUpdatePost(editing.id, editing);
      } else {
        await adminCreatePost(editing);
      }
      await loadPosts();
      setEditing(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"?`)) return;
    await adminDeletePost(post.id);
    await loadPosts();
  };

  const movePost = async (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= posts.length) return;
    const next = [...posts];
    [next[idx], next[target]] = [next[target], next[idx]];
    setPosts(next); // optimistic
    try {
      await adminReorderPosts(next.map((p) => p.id));
    } catch (e) {
      await loadPosts(); // revert on failure
    }
  };

  const featuredCount = posts.filter((p) => p.featured).length;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const setField = (k, v) => setEditing((prev) => ({ ...prev, [k]: v }));

  return (
    <div
      className="h-screen w-screen overflow-hidden bg-white text-zinc-900 flex flex-col"
      data-testid="admin-page"
    >
      {/* Top bar */}
      <header className="flex items-center justify-between px-6 md:px-10 py-4 border-b border-black/10">
        <div className="flex items-center gap-6">
          <a
            href="/"
            className="flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.22em] hover:text-[#002FA7] transition-colors"
          >
            <ArrowLeft size={14} strokeWidth={2} /> Site
          </a>
          <span className="font-display text-xs font-bold uppercase tracking-[0.22em] text-zinc-400">
            Admin / {user?.email}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="tab-posts"
            onClick={() => setTab("posts")}
            className={`flex items-center gap-2 px-3 py-2 font-display text-[10px] font-bold uppercase tracking-[0.2em] border ${
              tab === "posts"
                ? "bg-[#0A0A0A] text-white border-black"
                : "bg-white border-black/10 hover:border-black"
            }`}
          >
            <FileText size={13} strokeWidth={2} /> Posts ({posts.length})
          </button>
          <button
            data-testid="tab-subscribers"
            onClick={() => setTab("subscribers")}
            className={`flex items-center gap-2 px-3 py-2 font-display text-[10px] font-bold uppercase tracking-[0.2em] border ${
              tab === "subscribers"
                ? "bg-[#0A0A0A] text-white border-black"
                : "bg-white border-black/10 hover:border-black"
            }`}
          >
            <Users size={13} strokeWidth={2} /> Subs ({subscribers.length})
          </button>
          <button
            data-testid="tab-site"
            onClick={() => setTab("site")}
            className={`flex items-center gap-2 px-3 py-2 font-display text-[10px] font-bold uppercase tracking-[0.2em] border ${
              tab === "site"
                ? "bg-[#0A0A0A] text-white border-black"
                : "bg-white border-black/10 hover:border-black"
            }`}
          >
            <SettingsIcon size={13} strokeWidth={2} /> Site
          </button>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 font-display text-[10px] font-bold uppercase tracking-[0.2em] border border-black/10 hover:border-[#FF2A00] hover:text-[#FF2A00] transition-colors"
          >
            <LogOut size={13} strokeWidth={2} /> Logout
          </button>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 overflow-hidden">
        {tab === "posts" ? (
          editing ? (
            <Editor
              editing={editing}
              setField={setField}
              save={save}
              cancel={cancelEdit}
              saving={saving}
              error={error}
              previewMode={previewMode}
              setPreviewMode={setPreviewMode}
            />
          ) : (
            <PostsList
              posts={posts}
              onNew={startNew}
              onEdit={startEdit}
              onDelete={remove}
              onMove={movePost}
              featuredCount={featuredCount}
            />
          )
        ) : tab === "subscribers" ? (
          <SubscribersList
            subs={subscribers}
            refresh={loadSubs}
          />
        ) : (
          <SiteSettings settings={settings} refresh={refreshSettings} />
        )}
      </div>
    </div>
  );
}

const PostsList = ({ posts, onNew, onEdit, onDelete, onMove, featuredCount }) => (
  <div className="h-full overflow-y-auto px-6 md:px-10 py-8">
    <div className="flex items-center justify-between mb-3 max-w-5xl mx-auto">
      <h1 className="font-display font-black text-4xl tracking-[-0.03em]">
        Blog Posts
      </h1>
      <button
        data-testid="new-post-btn"
        onClick={onNew}
        className="flex items-center gap-2 px-4 py-2.5 bg-[#0A0A0A] text-white font-display text-[10px] font-bold uppercase tracking-[0.22em] hover:bg-[#002FA7] transition-colors"
      >
        <Plus size={14} strokeWidth={2.5} /> New Post
      </button>
    </div>

    <p className="max-w-5xl mx-auto text-zinc-500 text-xs mb-8">
      ★ Featured ({featuredCount}/6) appear as floating tiles on the homepage.
      Use ↑ ↓ arrows to reorder how posts appear on the Blog Posts page.
    </p>

    {posts.length === 0 ? (
      <div className="max-w-5xl mx-auto border border-dashed border-black/30 p-16 text-center">
        <p className="font-display text-sm uppercase tracking-[0.2em] text-zinc-500 mb-2">
          No blog posts yet
        </p>
        <p className="text-zinc-400 text-sm">
          Click "New Post" to publish your first one.
        </p>
      </div>
    ) : (
      <div className="max-w-5xl mx-auto border border-black/10">
        {posts.map((p, idx) => (
          <div
            key={p.id}
            data-testid={`admin-post-${p.slug}`}
            className="flex items-center justify-between gap-3 px-5 py-3 border-b border-black/10 last:border-b-0 hover:bg-zinc-50 transition-colors"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="flex flex-col gap-0.5 shrink-0">
                <button
                  data-testid={`move-up-${p.slug}`}
                  onClick={() => onMove(idx, -1)}
                  disabled={idx === 0}
                  aria-label="Move up"
                  className="p-1 border border-black/10 hover:border-black hover:bg-black hover:text-white transition-colors disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-current disabled:cursor-not-allowed"
                >
                  <ChevronUp size={11} strokeWidth={2.5} />
                </button>
                <button
                  data-testid={`move-down-${p.slug}`}
                  onClick={() => onMove(idx, 1)}
                  disabled={idx === posts.length - 1}
                  aria-label="Move down"
                  className="p-1 border border-black/10 hover:border-black hover:bg-black hover:text-white transition-colors disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-current disabled:cursor-not-allowed"
                >
                  <ChevronDown size={11} strokeWidth={2.5} />
                </button>
              </div>

              <span className="font-display text-[10px] font-bold tracking-[0.2em] text-zinc-400 w-8 shrink-0">
                {String(idx + 1).padStart(2, "0")}
              </span>

              <span
                className={`text-[9px] font-display font-bold uppercase tracking-[0.2em] px-2 py-0.5 border shrink-0 ${
                  p.published
                    ? "border-[#002FA7] text-[#002FA7]"
                    : "border-zinc-300 text-zinc-400"
                }`}
              >
                {p.published ? "Live" : "Draft"}
              </span>

              {p.featured && (
                <span
                  data-testid={`featured-${p.slug}`}
                  className="text-[9px] font-display font-bold uppercase tracking-[0.2em] px-2 py-0.5 border border-[#FF2A00] text-[#FF2A00] shrink-0"
                  title="Featured on homepage"
                >
                  ★ Home
                </span>
              )}

              <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 shrink-0 hidden md:inline">
                {p.tag}
              </span>
              <span className="font-medium text-sm truncate">{p.title}</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                data-testid={`edit-${p.slug}`}
                onClick={() => onEdit(p)}
                aria-label="Edit"
                className="p-2 hover:bg-black hover:text-white transition-colors"
              >
                <Edit3 size={14} strokeWidth={2} />
              </button>
              <button
                data-testid={`delete-${p.slug}`}
                onClick={() => onDelete(p)}
                aria-label="Delete"
                className="p-2 hover:bg-[#FF2A00] hover:text-white transition-colors"
              >
                <Trash2 size={14} strokeWidth={2} />
              </button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const Editor = ({
  editing,
  setField,
  save,
  cancel,
  saving,
  error,
  previewMode,
  setPreviewMode,
}) => (
  <div className="h-full overflow-y-auto px-6 md:px-10 py-8">
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={cancel}
          className="flex items-center gap-2 font-display text-[10px] font-bold uppercase tracking-[0.22em] hover:text-[#002FA7]"
        >
          <ArrowLeft size={14} strokeWidth={2} /> Back to list
        </button>
        <div className="flex items-center gap-2">
          <button
            data-testid="toggle-preview"
            onClick={() => setPreviewMode((v) => !v)}
            className="flex items-center gap-2 px-3 py-2 border border-black/10 font-display text-[10px] font-bold uppercase tracking-[0.2em] hover:border-black"
          >
            {previewMode ? (
              <>
                <EyeOff size={13} /> Edit
              </>
            ) : (
              <>
                <Eye size={13} /> Preview
              </>
            )}
          </button>
          <button
            data-testid="save-post"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white font-display text-[10px] font-bold uppercase tracking-[0.22em] hover:bg-[#002FA7] disabled:opacity-60"
          >
            <Save size={13} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {error && (
        <p className="text-[#FF2A00] text-xs font-medium mb-4">{error}</p>
      )}

      {previewMode ? (
        <article className="prose prose-zinc max-w-none">
          <span className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-[#002FA7]">
            {editing.tag}
          </span>
          <h1 className="font-display font-black text-5xl tracking-[-0.03em] leading-[0.95] mt-4">
            {editing.title || "Untitled"}
          </h1>
          {editing.snippet && (
            <p className="text-lg text-zinc-700 border-l-2 border-[#002FA7] pl-5 italic">
              {editing.snippet}
            </p>
          )}
          {editing.thumb && (
            <div className="aspect-[16/9] w-full overflow-hidden border border-black/10 my-8 not-prose">
              <img
                src={resolveImageUrl(editing.thumb)}
                alt=""
                className="h-full w-full object-cover"
              />
            </div>
          )}
          <div
            className="rich-content"
            dangerouslySetInnerHTML={{
              __html: editing.body || "<p><em>No content yet…</em></p>",
            }}
          />
        </article>
      ) : (
        <div className="space-y-4">
          <Field label="Title">
            <input
              data-testid="post-title"
              value={editing.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="The first principle of…"
              className="w-full px-4 py-3 text-2xl font-display font-bold tracking-tight border border-black/90 outline-none focus:border-[#002FA7]"
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Tag">
              <input
                data-testid="post-tag"
                value={editing.tag}
                onChange={(e) => setField("tag", e.target.value)}
                className="w-full px-4 py-3 text-sm border border-black/90 outline-none focus:border-[#002FA7]"
              />
            </Field>
            <Field label="Cover image" className="md:col-span-2">
              <ThumbUploader
                value={editing.thumb}
                onChange={(v) => setField("thumb", v)}
              />
            </Field>
          </div>
          <Field label="Snippet (1–2 lines, shown on tile + above body)">
            <input
              data-testid="post-snippet"
              value={editing.snippet}
              onChange={(e) => setField("snippet", e.target.value)}
              className="w-full px-4 py-3 text-sm border border-black/90 outline-none focus:border-[#002FA7]"
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Cover image caption (shown under the cover photo)">
              <input
                data-testid="post-cover-caption"
                value={editing.cover_caption || ""}
                onChange={(e) => setField("cover_caption", e.target.value)}
                placeholder="e.g. Photo: Anthill Ventures office, Hyderabad"
                className="w-full px-4 py-3 text-sm border border-black/90 outline-none focus:border-[#002FA7]"
              />
            </Field>
            <Field label="Display date (top-right of post — leave blank to use creation date)">
              <input
                data-testid="post-display-date"
                value={editing.display_date || ""}
                onChange={(e) => setField("display_date", e.target.value)}
                placeholder="e.g. 27 February 2026"
                className="w-full px-4 py-3 text-sm border border-black/90 outline-none focus:border-[#002FA7]"
              />
            </Field>
          </div>
          <Field label="Body — full formatting (bold, italic, underline, color, highlight, headings, fonts, lists, links, images)">
            <RichTextEditor
              value={editing.body}
              onChange={(html) => setField("body", html)}
              placeholder="Write your blog post… use the toolbar above to format."
            />
          </Field>

          {/* Per-post author override */}
          <details className="border border-black/10 bg-zinc-50 px-4 py-3 group">
            <summary className="cursor-pointer font-display text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-700 hover:text-black list-none flex items-center justify-between">
              <span>👤 Custom author for this post (optional)</span>
              <ChevronDown
                size={14}
                strokeWidth={2.5}
                className="group-open:rotate-180 transition-transform"
              />
            </summary>
            <div className="mt-4 space-y-3">
              <p className="text-[11px] text-zinc-500">
                Leave any field blank to use the site default. Useful for guest
                posts or co-authored pieces.
              </p>
              <Field label="Author name (override)">
                <input
                  data-testid="post-author-name"
                  value={editing.author_name_override || ""}
                  onChange={(e) => setField("author_name_override", e.target.value)}
                  placeholder="Leave blank to use default"
                  className="w-full px-4 py-2.5 text-sm border border-black/90 outline-none focus:border-[#002FA7]"
                />
              </Field>
              <Field label="Author LinkedIn URL (override)">
                <input
                  data-testid="post-author-linkedin"
                  value={editing.author_linkedin_override || ""}
                  onChange={(e) => setField("author_linkedin_override", e.target.value)}
                  placeholder="https://linkedin.com/in/…"
                  className="w-full px-4 py-2.5 text-sm border border-black/90 outline-none focus:border-[#002FA7]"
                />
              </Field>
              <Field label="Author photo (override)">
                <ThumbUploader
                  value={editing.author_image_override}
                  onChange={(v) => setField("author_image_override", v)}
                />
              </Field>
              <Field label="Author bio (override)">
                <textarea
                  data-testid="post-author-bio"
                  value={editing.author_bio_override || ""}
                  onChange={(e) => setField("author_bio_override", e.target.value)}
                  rows={2}
                  placeholder="Leave blank to use default"
                  className="w-full px-4 py-2.5 text-sm border border-black/90 outline-none focus:border-[#002FA7] leading-relaxed"
                />
              </Field>
            </div>
          </details>

          {/* SEO / meta */}
          <details className="border border-black/10 bg-zinc-50 px-4 py-3 group" data-testid="post-seo-section">
            <summary className="cursor-pointer font-display text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-700 hover:text-black list-none flex items-center justify-between">
              <span>🔎 SEO — Meta title & description (Google results)</span>
              <ChevronDown size={14} strokeWidth={2.5} className="group-open:rotate-180 transition-transform" />
            </summary>
            <div className="mt-4 space-y-3">
              <p className="text-[11px] text-zinc-500">
                Overrides the title/description that appear in Google search results. These tags are invisible on the page — the live preview below shows how Google will render them.
              </p>
              <Field label="Meta title (recommended: 50–60 characters)">
                <input
                  data-testid="post-meta-title"
                  value={editing.meta_title || ""}
                  onChange={(e) => setField("meta_title", e.target.value)}
                  placeholder="e.g. Why patient capital wins — Adi Notes"
                  className="w-full px-4 py-2.5 text-sm border border-black/90 outline-none focus:border-[#002FA7]"
                />
              </Field>
              <Field label="Meta description (recommended: 140–160 characters)">
                <textarea
                  data-testid="post-meta-description"
                  value={editing.meta_description || ""}
                  onChange={(e) => setField("meta_description", e.target.value)}
                  rows={2}
                  placeholder="One clear sentence that will show under the title in Google."
                  className="w-full px-4 py-2.5 text-sm border border-black/90 outline-none focus:border-[#002FA7] leading-relaxed"
                />
              </Field>
              <GooglePreview post={editing} />
            </div>
          </details>

          {/* Open Graph — social share previews */}
          <details className="border border-black/10 bg-zinc-50 px-4 py-3 group" data-testid="post-og-section">
            <summary className="cursor-pointer font-display text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-700 hover:text-black list-none flex items-center justify-between">
              <span>🔗 Social share preview (LinkedIn / X / iMessage)</span>
              <ChevronDown size={14} strokeWidth={2.5} className="group-open:rotate-180 transition-transform" />
            </summary>
            <div className="mt-4 space-y-3">
              <p className="text-[11px] text-zinc-500">
                Controls exactly what appears when someone pastes your share link into LinkedIn, Twitter/X, or iMessage. Falls back to Meta / snippet / cover image when blank. The live preview below shows the resolved values.
              </p>
              <Field label="OG title (falls back to meta title or post title)">
                <input
                  data-testid="post-og-title"
                  value={editing.og_title || ""}
                  onChange={(e) => setField("og_title", e.target.value)}
                  placeholder="Optional — override the share card headline"
                  className="w-full px-4 py-2.5 text-sm border border-black/90 outline-none focus:border-[#002FA7]"
                />
              </Field>
              <Field label="OG description (falls back to meta description or snippet)">
                <textarea
                  data-testid="post-og-description"
                  value={editing.og_description || ""}
                  onChange={(e) => setField("og_description", e.target.value)}
                  rows={2}
                  placeholder="Optional — override the share card sub-text"
                  className="w-full px-4 py-2.5 text-sm border border-black/90 outline-none focus:border-[#002FA7] leading-relaxed"
                />
              </Field>
              <Field label="OG image (recommended 1200×630) — falls back to cover image">
                <ThumbUploader
                  value={editing.og_image}
                  onChange={(v) => setField("og_image", v)}
                  testId="post-og-image"
                  uploadTestId="post-og-image-upload"
                />
              </Field>
              <SocialPreview post={editing} />
            </div>
          </details>

          {/* Embeds — structured blocks */}
          <details className="border border-black/10 bg-zinc-50 px-4 py-3 group" data-testid="post-embeds-section">
            <summary className="cursor-pointer font-display text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-700 hover:text-black list-none flex items-center justify-between">
              <span>▶ Embeds — Figma / Loom / YouTube / X / Spotify</span>
              <ChevronDown size={14} strokeWidth={2.5} className="group-open:rotate-180 transition-transform" />
            </summary>
            <div className="mt-4">
              <EmbedsEditor
                embeds={editing.embeds || []}
                onChange={(list) => setField("embeds", list)}
              />
            </div>
          </details>

          {/* Lead magnet */}
          <details className="border border-black/10 bg-zinc-50 px-4 py-3 group" data-testid="post-lead-magnet-section">
            <summary className="cursor-pointer font-display text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-700 hover:text-black list-none flex items-center justify-between">
              <span>🎁 Lead magnet — auto-email a download on subscribe</span>
              <ChevronDown size={14} strokeWidth={2.5} className="group-open:rotate-180 transition-transform" />
            </summary>
            <div className="mt-4 space-y-3">
              <p className="text-[11px] text-zinc-500">
                When set, a compact subscribe form appears at the bottom of this post. Anyone who subscribes there gets an automatic email with the download link.
              </p>
              <Field label="Download URL (Google Drive / Dropbox / Notion / direct PDF link)">
                <input
                  data-testid="post-lead-magnet-url"
                  value={editing.lead_magnet_url || ""}
                  onChange={(e) => setField("lead_magnet_url", e.target.value)}
                  placeholder="https://…"
                  className="w-full px-4 py-2.5 text-sm border border-black/90 outline-none focus:border-[#002FA7]"
                />
              </Field>
              <Field label="Button / offer label (e.g. 'Grab the deck', 'Get the checklist')">
                <input
                  data-testid="post-lead-magnet-label"
                  value={editing.lead_magnet_label || ""}
                  onChange={(e) => setField("lead_magnet_label", e.target.value)}
                  placeholder="Download the deck"
                  className="w-full px-4 py-2.5 text-sm border border-black/90 outline-none focus:border-[#002FA7]"
                />
              </Field>
            </div>
          </details>

          {/* Secret preview URL — only for saved posts */}
          {editing.preview_token && (
            <PreviewLinkRow token={editing.preview_token} />
          )}

          <label className="flex items-center gap-3 pt-2">
            <input
              data-testid="post-published"
              type="checkbox"
              checked={editing.published}
              onChange={(e) => setField("published", e.target.checked)}
              className="w-4 h-4 accent-[#002FA7]"
            />
            <span className="font-display text-[10px] font-bold uppercase tracking-[0.22em]">
              Published (visible on homepage)
            </span>
          </label>

          <label className="flex items-start gap-3 pt-1">
            <input
              data-testid="post-featured"
              type="checkbox"
              checked={!!editing.featured}
              onChange={(e) => setField("featured", e.target.checked)}
              className="w-4 h-4 mt-0.5 accent-[#FF2A00]"
            />
            <span className="font-display text-[10px] font-bold uppercase tracking-[0.22em] leading-relaxed">
              ★ Feature on homepage as a floating tile
              <span className="block normal-case font-medium tracking-normal text-zinc-400 mt-0.5">
                Pick up to 6 posts to show as drifting tiles on the home page.
              </span>
            </span>
          </label>

          {!editing.notified_at && (
            <label className="flex items-start gap-3 pt-1">
              <input
                data-testid="post-notify"
                type="checkbox"
                checked={editing.notify !== false}
                onChange={(e) => setField("notify", e.target.checked)}
                className="w-4 h-4 mt-0.5 accent-[#FF2A00]"
              />
              <span className="font-display text-[10px] font-bold uppercase tracking-[0.22em] leading-relaxed">
                Email all subscribers when this is published
                <span className="block normal-case font-medium tracking-normal text-zinc-400 mt-0.5">
                  Sent once on first publish only — never re-sent on edits.
                </span>
              </span>
            </label>
          )}

          {editing.notified_at && (
            <div className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-600 mt-2">
              ✓ Subscribers notified on{" "}
              {new Date(editing.notified_at).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  </div>
);

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-1.5 block">
      {label}
    </span>
    {children}
  </label>
);

const EMBED_TYPES = [
  { value: "figma", label: "Figma" },
  { value: "loom", label: "Loom" },
  { value: "youtube", label: "YouTube" },
  { value: "twitter", label: "X / Twitter" },
  { value: "spotify", label: "Spotify (podcast / track)" },
];

const EmbedsEditor = ({ embeds, onChange }) => {
  const update = (i, patch) => {
    const next = embeds.map((e, idx) => (idx === i ? { ...e, ...patch } : e));
    onChange(next);
  };
  const remove = (i) => onChange(embeds.filter((_, idx) => idx !== i));
  const add = () =>
    onChange([...embeds, { type: "loom", url: "", caption: "" }]);

  const [copiedIdx, setCopiedIdx] = useState(null);
  const copyShortcode = async (i) => {
    const code = `[[embed:${i}]]`;
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      /* ignore — best effort */
    }
    setCopiedIdx(i);
    setTimeout(() => setCopiedIdx(null), 1800);
  };

  return (
    <div className="space-y-3" data-testid="embeds-editor">
      {embeds.length === 0 && (
        <p className="text-[11px] text-zinc-500">
          No embeds yet. Add a Figma prototype, Loom walkthrough, YouTube video, X thread, or Spotify episode.
        </p>
      )}

      {embeds.length > 0 && (
        <div className="border border-[#002FA7]/30 bg-[#002FA7]/[0.04] p-3 text-[11px] text-zinc-700 leading-relaxed">
          <strong className="font-display font-bold uppercase text-[10px] tracking-[0.18em] text-[#002FA7]">
            Where each embed appears
          </strong>
          <p className="mt-1">
            Paste the shortcode (e.g. <code className="bg-white px-1 py-0.5 border">[[embed:0]]</code>) anywhere in your post body — the embed will render at that exact spot. Any embeds whose shortcode isn't in the body will render at the bottom of the post.
          </p>
        </div>
      )}

      {embeds.map((em, i) => (
        <div
          key={i}
          data-testid={`embed-row-${i}`}
          className="border border-black/15 bg-white p-3 space-y-2"
        >
          <div className="flex items-center gap-2">
            <select
              data-testid={`embed-type-${i}`}
              value={em.type || "loom"}
              onChange={(e) => update(i, { type: e.target.value })}
              className="text-sm border border-black/20 px-2 py-1.5 font-display font-medium"
            >
              {EMBED_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <input
              data-testid={`embed-url-${i}`}
              value={em.url || ""}
              onChange={(e) => update(i, { url: e.target.value })}
              placeholder="Paste the share/embed URL"
              className="flex-1 px-3 py-1.5 text-sm border border-black/20 outline-none focus:border-[#002FA7]"
            />
            <button
              type="button"
              data-testid={`embed-remove-${i}`}
              onClick={() => remove(i)}
              aria-label="Remove embed"
              className="p-1.5 border border-black/15 hover:border-[#FF2A00] hover:text-[#FF2A00]"
            >
              <Trash size={14} strokeWidth={2} />
            </button>
          </div>
          <input
            data-testid={`embed-caption-${i}`}
            value={em.caption || ""}
            onChange={(e) => update(i, { caption: e.target.value })}
            placeholder="Optional caption"
            className="w-full px-3 py-1.5 text-xs border border-black/10 outline-none focus:border-[#002FA7] italic"
          />
          <div className="flex items-center gap-2 pt-1">
            <code className="text-[11px] px-2 py-1 bg-zinc-100 border border-black/10 font-mono">
              [[embed:{i}]]
            </code>
            <button
              type="button"
              data-testid={`embed-copy-shortcode-${i}`}
              onClick={() => copyShortcode(i)}
              className={`flex items-center gap-1.5 px-2 py-1 border font-display text-[10px] font-bold uppercase tracking-[0.16em] ${
                copiedIdx === i
                  ? "border-emerald-600 text-emerald-600"
                  : "border-black/40 hover:bg-black hover:text-white"
              }`}
            >
              {copiedIdx === i ? (
                <>
                  <Check size={11} strokeWidth={2.5} /> Copied
                </>
              ) : (
                <>
                  <Copy size={11} strokeWidth={2.5} /> Copy shortcode
                </>
              )}
            </button>
            <span className="text-[10px] text-zinc-500">
              Paste this into your body to control placement.
            </span>
          </div>
        </div>
      ))}
      <button
        type="button"
        data-testid="embed-add"
        onClick={add}
        className="flex items-center gap-2 px-3 py-2 border border-black/90 font-display text-[10px] font-bold uppercase tracking-[0.22em] hover:bg-[#0A0A0A] hover:text-white"
      >
        <Plus size={13} strokeWidth={2.5} /> Add embed
      </button>
    </div>
  );
};

// Live "Google search result" preview — visualises how the post will render
// in Google SERPs given the resolved meta title / description.
const GooglePreview = ({ post }) => {
  const { settings } = useSettings();
  const brand = settings.brand_name || "";
  const title =
    (post.meta_title || "").trim() ||
    (post.og_title || "").trim() ||
    post.title ||
    "Untitled";
  const desc =
    (post.meta_description || "").trim() ||
    (post.og_description || "").trim() ||
    (post.snippet || "").trim() ||
    "This description will show under the title in Google results. Add a meta description above to control it.";
  const displayTitle = brand ? `${title} — ${brand}` : title;
  const url = post.slug ? `naielkhatri.com › ${post.slug}` : "naielkhatri.com › ...";
  return (
    <div
      data-testid="seo-google-preview"
      className="border border-black/10 bg-white p-4 space-y-1"
    >
      <p className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-2">
        Preview — Google search result
      </p>
      <p className="text-xs text-zinc-600">{url}</p>
      <p
        className="text-[#1a0dab] text-lg leading-tight truncate"
        style={{ fontFamily: "arial, sans-serif" }}
      >
        {displayTitle}
      </p>
      <p
        className="text-sm text-zinc-700 leading-snug line-clamp-2"
        style={{ fontFamily: "arial, sans-serif" }}
      >
        {desc}
      </p>
    </div>
  );
};

// Live "LinkedIn/Twitter share card" preview — visualises the OG card.
const SocialPreview = ({ post }) => {
  const title =
    (post.og_title || "").trim() ||
    (post.meta_title || "").trim() ||
    post.title ||
    "Untitled";
  const desc =
    (post.og_description || "").trim() ||
    (post.meta_description || "").trim() ||
    (post.snippet || "").trim() ||
    "Add an OG description above.";
  const image = resolveImageUrl(post.og_image || post.thumb || "");
  return (
    <div
      data-testid="seo-social-preview"
      className="border border-black/10 bg-white p-4"
    >
      <p className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-3">
        Preview — LinkedIn / X share card
      </p>
      <div className="border border-zinc-200 max-w-md">
        {image ? (
          <div className="aspect-[1.91/1] w-full bg-zinc-100 overflow-hidden">
            <img
              src={image}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="aspect-[1.91/1] w-full bg-zinc-100 flex items-center justify-center">
            <p className="text-xs text-zinc-400">Add an OG image (1200×630) to see it here</p>
          </div>
        )}
        <div className="p-3 bg-zinc-50 border-t border-zinc-200">
          <p className="text-[10px] uppercase text-zinc-500 mb-1 tracking-wider">
            naielkhatri.com
          </p>
          <p className="font-semibold text-sm text-zinc-900 line-clamp-2 leading-tight">
            {title}
          </p>
          <p className="text-xs text-zinc-600 line-clamp-2 mt-1 leading-snug">
            {desc}
          </p>
        </div>
      </div>
    </div>
  );
};

const PreviewLinkRow = ({ token }) => {
  const [copied, setCopied] = useState(false);
  const url = buildPreviewUrl(token);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this link:", url);
    }
  };
  return (
    <div
      data-testid="post-preview-link"
      className="border border-dashed border-[#002FA7]/40 bg-[#002FA7]/[0.03] px-4 py-3"
    >
      <p className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-[#002FA7] mb-1 flex items-center gap-2">
        <Link2 size={13} strokeWidth={2.5} /> Secret preview link
      </p>
      <p className="text-[11px] text-zinc-500 mb-2">
        Share this URL with mentors or editors to let them read this post without publishing it. Works even while <em>Published</em> is unchecked.
      </p>
      <div className="flex items-center gap-2">
        <input
          readOnly
          value={url}
          className="flex-1 px-3 py-1.5 text-xs border border-black/20 bg-white font-mono"
          onFocus={(e) => e.target.select()}
        />
        <button
          type="button"
          data-testid="post-preview-copy"
          onClick={copy}
          className={`flex items-center gap-2 px-3 py-1.5 border font-display text-[10px] font-bold uppercase tracking-[0.18em] ${
            copied
              ? "border-emerald-600 text-emerald-600"
              : "border-black hover:bg-black hover:text-white"
          }`}
        >
          {copied ? (
            <>
              <Check size={12} strokeWidth={2.5} /> Copied
            </>
          ) : (
            <>
              <Copy size={12} strokeWidth={2.5} /> Copy
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const ThumbUploader = ({ value, onChange, testId = "post-thumb-url", uploadTestId = "thumb-upload-btn" }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = React.useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const res = await adminUploadImage(file);
      onChange(res.url);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          data-testid={testId}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste URL or upload →"
          className="flex-1 px-4 py-3 text-sm border border-black/90 outline-none focus:border-[#002FA7]"
        />
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleFile}
          className="hidden"
        />
        <button
          type="button"
          data-testid={uploadTestId}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-4 py-3 border border-black/90 font-display text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors disabled:opacity-60"
        >
          <Upload size={13} strokeWidth={2} />
          {uploading ? "Uploading…" : "Upload"}
        </button>
      </div>
      {error && <p className="text-[#FF2A00] text-xs font-medium">{error}</p>}
      {value && (
        <div className="w-32 h-20 overflow-hidden border border-black/10">
          <img
            src={resolveImageUrl(value)}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      )}
    </div>
  );
};

const BodyEditor = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = React.useRef(null);
  const textareaRef = React.useRef(null);

  const insertAtCursor = (text) => {
    const ta = textareaRef.current;
    if (!ta) {
      onChange((value || "") + text);
      return;
    }
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    const next = (value || "").slice(0, start) + text + (value || "").slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      const cursor = start + text.length;
      ta.setSelectionRange(cursor, cursor);
    });
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const res = await adminUploadImage(file);
      const alt = file.name.replace(/\.[^.]+$/, "");
      insertAtCursor(`\n\n![${alt}](${res.url})\n\n`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleFile}
          className="hidden"
          data-testid="body-image-input"
        />
        <button
          type="button"
          data-testid="insert-image-btn"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-3 py-1.5 border border-black/90 font-display text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors disabled:opacity-60"
        >
          <ImageIcon size={12} strokeWidth={2} />
          {uploading ? "Uploading…" : "Insert image"}
        </button>
        <span className="text-[10px] text-zinc-400">
          Markdown supported. Images upload directly to your blog.
        </span>
      </div>
      {error && <p className="text-[#FF2A00] text-xs font-medium">{error}</p>}
      <textarea
        ref={textareaRef}
        data-testid="post-body"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={16}
        placeholder={"# Heading\n\nWrite the article here.\n\n- Use **markdown**\n- Add [links](https://example.com)\n- Click 'Insert image' above to upload pictures"}
        className="w-full px-4 py-3 text-sm font-mono border border-black/90 outline-none focus:border-[#002FA7] leading-relaxed"
      />
    </div>
  );
};

const SubscribersList = ({ subs, refresh }) => {
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editEmail, setEditEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const startAdd = () => {
    setAdding(true);
    setNewEmail("");
    setError("");
  };

  const cancelAdd = () => {
    setAdding(false);
    setNewEmail("");
    setError("");
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setBusy(true);
    setError("");
    try {
      await adminAddSubscriber(newEmail.trim());
      await refresh();
      cancelAdd();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditEmail(s.email);
    setError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditEmail("");
    setError("");
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editEmail.trim()) return;
    setBusy(true);
    setError("");
    try {
      await adminUpdateSubscriber(editingId, editEmail.trim());
      await refresh();
      cancelEdit();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.email} from your subscribers?`)) return;
    setBusy(true);
    try {
      await adminDeleteSubscriber(s.id);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto px-6 md:px-10 py-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display font-black text-4xl tracking-[-0.03em]">
              Subscribers
            </h1>
            <p className="text-zinc-500 text-xs mt-1">
              {subs.length} total — add manually, edit, or remove anyone.
            </p>
          </div>
          {!adding && (
            <button
              data-testid="add-sub-btn"
              onClick={startAdd}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#0A0A0A] text-white font-display text-[10px] font-bold uppercase tracking-[0.22em] hover:bg-[#002FA7] transition-colors"
            >
              <Plus size={14} strokeWidth={2.5} /> Add subscriber
            </button>
          )}
        </div>

        {adding && (
          <form
            onSubmit={submitAdd}
            data-testid="add-sub-form"
            className="border border-black/90 p-4 mb-6 flex items-center gap-2"
          >
            <input
              data-testid="add-sub-email"
              type="email"
              required
              autoFocus
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="email@example.com"
              className="flex-1 px-3 py-2 text-sm border border-black/20 outline-none focus:border-[#002FA7]"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="add-sub-save"
              className="flex items-center gap-1.5 px-4 py-2 bg-[#0A0A0A] text-white font-display text-[10px] font-bold uppercase tracking-[0.22em] hover:bg-[#002FA7] disabled:opacity-60"
            >
              <Check size={13} strokeWidth={2.5} /> Add
            </button>
            <button
              type="button"
              onClick={cancelAdd}
              className="flex items-center gap-1.5 px-4 py-2 border border-black/20 font-display text-[10px] font-bold uppercase tracking-[0.22em] hover:border-black"
            >
              Cancel
            </button>
          </form>
        )}

        {error && (
          <p className="text-[#FF2A00] text-xs font-medium mb-4">{error}</p>
        )}

        {subs.length === 0 ? (
          <div className="border border-dashed border-black/30 p-16 text-center">
            <p className="font-display text-sm uppercase tracking-[0.2em] text-zinc-500 mb-2">
              No subscribers yet
            </p>
            <p className="text-zinc-400 text-sm">
              Share your homepage to start collecting emails — or add some
              manually.
            </p>
          </div>
        ) : (
          <div className="border border-black/10">
            {subs.map((s, i) => (
              <div
                key={s.id}
                data-testid={`sub-row-${s.id}`}
                className="flex items-center justify-between gap-3 px-5 py-3 border-b border-black/10 last:border-b-0 hover:bg-zinc-50 transition-colors"
              >
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <span className="font-display text-[10px] font-bold tracking-[0.2em] text-zinc-400 w-8 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {editingId === s.id ? (
                    <form
                      onSubmit={submitEdit}
                      className="flex items-center gap-2 flex-1"
                    >
                      <input
                        data-testid={`edit-sub-email-${s.id}`}
                        type="email"
                        required
                        autoFocus
                        value={editEmail}
                        onChange={(e) => setEditEmail(e.target.value)}
                        className="flex-1 px-3 py-1.5 text-sm border border-black/90 outline-none focus:border-[#002FA7]"
                      />
                      <button
                        type="submit"
                        disabled={busy}
                        data-testid={`save-sub-${s.id}`}
                        aria-label="Save"
                        className="p-2 border border-black/90 hover:bg-black hover:text-white disabled:opacity-60"
                      >
                        <Check size={14} strokeWidth={2.5} />
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        aria-label="Cancel"
                        className="p-2 border border-black/10 hover:border-black"
                      >
                        <XIcon size={14} strokeWidth={2.5} />
                      </button>
                    </form>
                  ) : (
                    <span className="font-medium text-sm truncate">
                      {s.email}
                    </span>
                  )}
                </div>
                {editingId !== s.id && (
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-display text-[10px] uppercase tracking-[0.2em] text-zinc-400 hidden sm:inline">
                      {new Date(s.created_at).toLocaleDateString()}
                    </span>
                    <button
                      data-testid={`edit-sub-${s.id}`}
                      onClick={() => startEdit(s)}
                      aria-label="Edit"
                      className="p-2 hover:bg-black hover:text-white transition-colors"
                    >
                      <Edit3 size={14} strokeWidth={2} />
                    </button>
                    <button
                      data-testid={`delete-sub-${s.id}`}
                      onClick={() => remove(s)}
                      aria-label="Remove"
                      className="p-2 hover:bg-[#FF2A00] hover:text-white transition-colors"
                    >
                      <Trash2 size={14} strokeWidth={2} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


const SETTINGS_GROUPS = [
  {
    title: "Identity & Hero",
    fields: [
      { key: "brand_name", label: "Top-left brand text", type: "text" },
      { key: "blog_posts_label", label: "Top-right link label", type: "text" },
      { key: "hero_image", label: "Hero image (paste URL or upload)", type: "image" },
      { key: "hero_top_left", label: "Hero strip — bottom-left", type: "text" },
      { key: "hero_top_right", label: "Hero strip — bottom-right", type: "text" },
    ],
  },
  {
    title: "About Section",
    fields: [
      { key: "about_label", label: "Small label (above heading)", type: "text" },
      { key: "about_heading", label: "Main heading", type: "textarea", rows: 3 },
      {
        key: "about_body",
        label: "Bio paragraph (Markdown — use **bold** for emphasis)",
        type: "textarea",
        rows: 6,
      },
      { key: "subscribe_label", label: "Subscribe form label", type: "text" },
    ],
  },
  {
    title: "Footer — Contact & Social Links",
    fields: [
      { key: "contact_email", label: "Contact email (mailto)", type: "text" },
      { key: "hide_contact_email", label: "Hide contact email from footer", type: "checkbox" },
      { key: "social_linkedin", label: "LinkedIn URL", type: "text" },
      { key: "hide_social_linkedin", label: "Hide LinkedIn", type: "checkbox" },
      { key: "social_newsletter", label: "Newsletter URL", type: "text" },
      { key: "hide_social_newsletter", label: "Hide Newsletter", type: "checkbox" },
      { key: "social_instagram", label: "Instagram URL", type: "text" },
      { key: "hide_social_instagram", label: "Hide Instagram", type: "checkbox" },
      { key: "social_twitter", label: "Twitter URL", type: "text" },
      { key: "hide_social_twitter", label: "Hide Twitter", type: "checkbox" },
      { key: "social_biolink", label: "BioLink URL", type: "text" },
      { key: "hide_social_biolink", label: "Hide BioLink", type: "checkbox" },
    ],
  },
  {
    title: "Community Block (bottom of each blog post)",
    fields: [
      { key: "community_image", label: "Main image (300×300, displayed without a frame)", type: "image" },
      { key: "community_title", label: "Title (e.g. 'Boardroom Notes | VC Lens')", type: "text" },
      { key: "community_tagline", label: "Tagline (one or two lines)", type: "textarea", rows: 3 },
      { key: "community_social_text", label: "Social proof line (e.g. '1,049 connections are subscribed')", type: "text" },
      { key: "community_subscriber_text", label: "Subscriber count line (e.g. '1,168 subscribers')", type: "text" },
      { key: "community_cta_url", label: "CTA button URL (leave blank to hide button)", type: "text" },
      { key: "community_cta_label", label: "CTA button label", type: "text" },
    ],
  },
  {
    title: "Author Block (shown at the bottom of every blog post)",
    fields: [
      { key: "author_label", label: "Section label (default: 'Author' — change to 'Written by', 'By', etc.)", type: "text" },
      { key: "author_name", label: "Author name", type: "text" },
      { key: "author_linkedin", label: "Author LinkedIn URL (name on the post links here)", type: "text" },
      { key: "author_image", label: "Author photo (shown as a circle)", type: "image" },
      { key: "author_bio", label: "Author one-liner bio", type: "textarea", rows: 2 },
    ],
  },
  {
    title: "Blog Posts Page (The Index)",
    fields: [
      { key: "index_heading", label: "Page heading (e.g. 'The Index.')", type: "text" },
      { key: "index_subtitle", label: "Page subtitle / intro line", type: "textarea", rows: 2 },
      { key: "index_empty_title", label: "Empty state title (when no posts)", type: "text" },
      { key: "index_empty_body", label: "Empty state body (when no posts)", type: "textarea", rows: 2 },
    ],
  },
  {
    title: "About Me Page (/about)",
    fields: [
      { key: "about_hero_image", label: "About page hero image", type: "image" },
      { key: "about_hero_left", label: "Hero strip — bottom-left", type: "text" },
      { key: "about_hero_right", label: "Hero strip — bottom-right", type: "text" },
      { key: "about_floating_image", label: "Floating portrait image (3×3 inch tile)", type: "image" },
      { key: "about_me_heading", label: "About Me — heading", type: "textarea", rows: 2 },
      {
        key: "about_me_body",
        label: "About Me — body (Markdown supported)",
        type: "textarea",
        rows: 10,
      },
    ],
  },
];

const SiteSettings = ({ settings, refresh }) => {
  const [draft, setDraft] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setDraft(settings);
  }, [settings]);

  const setField = (k, v) => setDraft((prev) => ({ ...prev, [k]: v }));

  // Read a field value with the correct fallback for its type (booleans → false, others → "").
  const valueOf = (obj, f) =>
    f.type === "checkbox" ? !!obj[f.key] : obj[f.key] || "";

  const dirty = SETTINGS_GROUPS.flatMap((g) => g.fields).some(
    (f) => valueOf(draft, f) !== valueOf(settings, f),
  );

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const changed = {};
      SETTINGS_GROUPS.flatMap((g) => g.fields).forEach((f) => {
        const next = valueOf(draft, f);
        const prev = valueOf(settings, f);
        if (next !== prev) {
          changed[f.key] = next;
        }
      });
      if (Object.keys(changed).length === 0) return;
      await adminUpdateSettings(changed);
      await refresh();
      setSavedAt(new Date());
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto px-6 md:px-10 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8 sticky top-0 bg-white py-2 z-10">
          <div>
            <h1 className="font-display font-black text-4xl tracking-[-0.03em]">
              Site
            </h1>
            <p className="text-zinc-500 text-xs mt-1">
              Edit any text or link on the homepage. Changes go live the moment
              you save.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {savedAt && !dirty && (
              <span className="font-display text-[10px] uppercase tracking-[0.2em] text-emerald-600">
                ✓ Saved
              </span>
            )}
            <button
              data-testid="save-settings"
              onClick={save}
              disabled={saving || !dirty}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#0A0A0A] text-white font-display text-[10px] font-bold uppercase tracking-[0.22em] hover:bg-[#002FA7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={13} strokeWidth={2.5} />
              {saving ? "Saving…" : dirty ? "Save changes" : "No changes"}
            </button>
          </div>
        </div>

        {error && <p className="text-[#FF2A00] text-xs font-medium mb-4">{error}</p>}

        <div className="space-y-12">
          {SETTINGS_GROUPS.map((group) => (
            <section key={group.title}>
              <h2 className="font-display text-xs font-bold uppercase tracking-[0.24em] text-[#002FA7] mb-5 border-b border-black/10 pb-2">
                {group.title}
              </h2>
              <div className="space-y-4">
                {group.fields.map((f) => (
                  <Field key={f.key} label={f.label}>
                    {f.type === "textarea" ? (
                      <textarea
                        data-testid={`setting-${f.key}`}
                        value={draft[f.key] || ""}
                        onChange={(e) => setField(f.key, e.target.value)}
                        rows={f.rows || 4}
                        className="w-full px-4 py-3 text-sm border border-black/90 outline-none focus:border-[#002FA7] leading-relaxed"
                      />
                    ) : f.type === "image" ? (
                      <ThumbUploader
                        value={draft[f.key]}
                        onChange={(v) => setField(f.key, v)}
                      />
                    ) : f.type === "checkbox" ? (
                      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          data-testid={`setting-${f.key}`}
                          checked={!!draft[f.key]}
                          onChange={(e) => setField(f.key, e.target.checked)}
                          className="w-4 h-4 accent-[#002FA7] cursor-pointer"
                        />
                        <span className="font-display text-[11px] font-medium uppercase tracking-[0.16em] text-zinc-600">
                          {draft[f.key] ? "Hidden" : "Visible"}
                        </span>
                      </label>
                    ) : (
                      <input
                        data-testid={`setting-${f.key}`}
                        value={draft[f.key] || ""}
                        onChange={(e) => setField(f.key, e.target.value)}
                        className="w-full px-4 py-3 text-sm border border-black/90 outline-none focus:border-[#002FA7]"
                      />
                    )}
                  </Field>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="h-12" />
      </div>
    </div>
  );
};
